import { Field, FieldCheckbox, FieldInput, FieldLabel, FieldSelect } from '@ibe/ui';
import { type ReactNode } from 'react';
import {
  Controller,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';

import {
  PAYMENT_PROVIDER,
  PROVIDER_OPTIONS,
  type PaymentGatewayFormInput,
} from '../api/schemas.js';

export interface PaymentGatewayFieldsProps {
  readonly register: UseFormRegister<PaymentGatewayFormInput>;
  readonly errors: FieldErrors<PaymentGatewayFormInput>;
  readonly control: Control<PaymentGatewayFormInput>;
  readonly setValue: UseFormSetValue<PaymentGatewayFormInput>;
  readonly mode: 'create' | 'edit';
}

function Section({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <fieldset className="space-y-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <div className="space-y-1">
        <h2 className="text-body font-semibold">{title}</h2>
        {description === undefined ? null : (
          <p className="text-caption text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </fieldset>
  );
}

function Row({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

function WindcaveCredentials({
  register,
  errors,
}: {
  readonly register: UseFormRegister<PaymentGatewayFormInput>;
  readonly errors: FieldErrors<PaymentGatewayFormInput>;
}): React.JSX.Element {
  return (
    <>
      <Row>
        <Field error={errors.windcaveBaseUrl?.message} required>
          <FieldLabel>Base URL</FieldLabel>
          <FieldInput autoComplete="off" {...register('windcaveBaseUrl')} />
        </Field>
        <Field error={errors.windcaveUsername?.message} required>
          <FieldLabel>Username</FieldLabel>
          <FieldInput autoComplete="off" {...register('windcaveUsername')} />
        </Field>
      </Row>
      <Row>
        <Field error={errors.windcaveAuthKey?.message} required>
          <FieldLabel>Auth Key</FieldLabel>
          <FieldInput autoComplete="off" type="password" {...register('windcaveAuthKey')} />
        </Field>
      </Row>
    </>
  );
}

function StripeCredentials({
  register,
  errors,
}: {
  readonly register: UseFormRegister<PaymentGatewayFormInput>;
  readonly errors: FieldErrors<PaymentGatewayFormInput>;
}): React.JSX.Element {
  return (
    <Row>
      <Field error={errors.stripePublishableKey?.message} required>
        <FieldLabel>Publishable key</FieldLabel>
        <FieldInput autoComplete="off" {...register('stripePublishableKey')} />
      </Field>
      <Field error={errors.stripeSecretKey?.message} required>
        <FieldLabel>Secret key</FieldLabel>
        <FieldInput autoComplete="off" type="password" {...register('stripeSecretKey')} />
      </Field>
    </Row>
  );
}

function WestpacCredentials({
  register,
  errors,
}: {
  readonly register: UseFormRegister<PaymentGatewayFormInput>;
  readonly errors: FieldErrors<PaymentGatewayFormInput>;
}): React.JSX.Element {
  return (
    <>
      <Row>
        <Field error={errors.westpacMerchantId?.message} required>
          <FieldLabel>Merchant ID</FieldLabel>
          <FieldInput autoComplete="off" {...register('westpacMerchantId')} />
        </Field>
        <Field error={errors.westpacApiKey?.message} required>
          <FieldLabel>API key</FieldLabel>
          <FieldInput autoComplete="off" type="password" {...register('westpacApiKey')} />
        </Field>
      </Row>
      <Row>
        <Field error={errors.westpacPassword?.message} required>
          <FieldLabel>Password</FieldLabel>
          <FieldInput autoComplete="off" type="password" {...register('westpacPassword')} />
        </Field>
      </Row>
    </>
  );
}

export function PaymentGatewayFields({
  register,
  errors,
  control,
  setValue,
  mode,
}: PaymentGatewayFieldsProps): React.JSX.Element {
  const provider = useWatch<PaymentGatewayFormInput, 'provider'>({ control, name: 'provider' });

  const credentialDescription =
    mode === 'edit'
      ? 'Stored credentials are not shown. Leave all fields blank to keep them, or enter a complete set to replace them.'
      : 'Enter the provider-specific connection details. They will be encrypted at rest.';

  return (
    <div className="space-y-6">
      <Section title="Gateway setup">
        <Row>
          <Controller
            control={control}
            name="provider"
            render={({ field }) => (
              <Field error={errors.provider?.message} required>
                <FieldLabel>Provider</FieldLabel>
                <FieldSelect
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    setValue('windcaveBaseUrl', '');
                    setValue('windcaveUsername', '');
                    setValue('windcaveAuthKey', '');
                    setValue('stripePublishableKey', '');
                    setValue('stripeSecretKey', '');
                    setValue('westpacMerchantId', '');
                    setValue('westpacApiKey', '');
                    setValue('westpacPassword', '');
                  }}
                  onBlur={field.onBlur}
                  options={PROVIDER_OPTIONS}
                  placeholder="Select provider…"
                  disabled={mode === 'edit'}
                />
              </Field>
            )}
          />
        </Row>

        <Field error={errors.name?.message} required>
          <FieldLabel>Gateway name</FieldLabel>
          <FieldInput
            autoComplete="off"
            placeholder="Main property gateway"
            {...register('name')}
          />
        </Field>
      </Section>

      <Section
        title="Configuration"
        description="Set whether this gateway is active and whether it is the default for the chain."
      >
        <Row>
          <Field>
            <Controller
              control={control}
              name="isDefault"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Default gateway
                </FieldCheckbox>
              )}
            />
          </Field>
          <Field>
            <Controller
              control={control}
              name="isActive"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Active
                </FieldCheckbox>
              )}
            />
          </Field>
        </Row>
      </Section>

      {provider !== '' && (
        <Section title="Credentials" description={credentialDescription}>
          {provider === PAYMENT_PROVIDER.WINDCAVE && (
            <WindcaveCredentials register={register} errors={errors} />
          )}
          {provider === PAYMENT_PROVIDER.STRIPE && (
            <StripeCredentials register={register} errors={errors} />
          )}
          {provider === PAYMENT_PROVIDER.WESTPAC && (
            <WestpacCredentials register={register} errors={errors} />
          )}
          {provider === PAYMENT_PROVIDER.MANUAL && (
            <p className="text-body text-muted-foreground">
              Manual gateways do not need provider credentials.
            </p>
          )}
        </Section>
      )}
    </div>
  );
}
